/**
 * Единый host для command-сценариев Activity.
 * По `context.customKind` находит команду в реестре и рендерит ее
 * `FormComponent` в контексте текущего scope/payload.
 */
import { ActionIcon, Box, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import type { ActivityContext } from '../../types/activity-context';
import type { ActivitySlashCommandContext } from '../../types/activity-slash-command';
import { getActivityChatCommandById } from '@ayocore/activity/client/extensions';

type ActivityContextViewProps = {
  context: ActivityContext | null;
  onClose?: () => void;
};

/**
 * Универсальный контекстный блок над инпутом чата.
 * Рендерит surface зарегистрированной команды по `context.customKind`.
 */
export function ActivityContextView({ context, onClose }: ActivityContextViewProps) {
  if (!context) {
    return null;
  }

  const closeHandler = onClose ?? (() => {});
  const command = getActivityChatCommandById(context.customKind);
  const previewContext = context.preview?.commandContext ?? null;

  const commandContext: ActivitySlashCommandContext = {
    threadEntityType: previewContext?.threadEntityType ?? context.scope.entity_type,
    threadEntityUuid: previewContext?.threadEntityUuid ?? context.scope.entity_uuid,
    ...(previewContext?.source ? { source: previewContext.source } : {}),
    event: previewContext?.event ?? null,
    payload: previewContext?.payload ?? null,
  };

  return (
    <Box
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 8,
        background: 'var(--mantine-color-body)',
        padding: '6px 8px',
      }}
    >
      <Stack gap={4}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text
            size='xs'
            fw={700}
            c='green.8'
            style={{
              minWidth: 0,
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={context.title}
          >
            {context.title}
          </Text>
          {onClose ? (
            <ActionIcon
              variant='subtle'
              color='green'
              size='sm'
              aria-label='Закрыть контекст'
              onClick={onClose}
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null}
        </Box>

        {context.subtitle ? (
          <Text size='xs' c='dimmed' lineClamp={1}>
            {context.subtitle}
          </Text>
        ) : null}

        {command?.FormComponent ? (
          <command.FormComponent
            context={commandContext}
            onCancel={closeHandler}
            onSubmitted={closeHandler}
          />
        ) : (
          <Text size='xs' c='dimmed'>
            Контекст "{context.customKind}" подключен, но обработчик не найден.
          </Text>
        )}
      </Stack>
    </Box>
  );
}
